import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FollowersModal } from './FollowersModal';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { ChevronDown, ExternalLink, Users, Heart, Trophy, Crown, Hash } from 'lucide-react';
import { getOptimizedImageUrl, handleImageError, ImageErrorState } from '@/utils/imageUtils';
import { cn } from '@/lib/utils';
import EloScoreDisplay from '@/components/video/EloScoreDisplay';
import ProfileSocialLinksExpanded from '@/components/profile/ProfileSocialLinksExpanded';
import { useAuth } from '@/contexts/AuthContext';
import VerifiedBadge from '@/components/common/VerifiedBadge';
import ProfileActions from './ProfileActions';
import EditProfileForm from './EditProfileForm';
import { getManualCounts, sumManualCounts } from '@/services/social-manual-service';
import { useEloScore } from '@/hooks/use-elo-score';
interface ProfileLandingPageProps {
  profile: any;
  onViewGallery: () => void;
}
const ProfileLandingPage: React.FC<ProfileLandingPageProps> = ({
  profile,
  onViewGallery
}) => {
  const {
    user
  } = useAuth();
  const { eloScore } = useEloScore(profile?.id);
  const [imageError, setImageError] = useState<ImageErrorState>({
    retryCount: 0,
    hasError: false
  });

  // Fetch manual follower counts and compute aggregated total
  const { data: manualCounts } = useQuery({
    queryKey: ['profile-manual-counts', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return {} as Record<string, number>;
      return getManualCounts(profile.id);
    },
    enabled: !!profile?.id
  });
  const globalFollowers = manualCounts ? sumManualCounts(manualCounts) : 0;
  const [imageKey, setImageKey] = useState(Date.now());
  const [showSocialLinks, setShowSocialLinks] = useState(false);
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [modalTab, setModalTab] = useState<"followers" | "following">("followers");
  const [isEditing, setIsEditing] = useState(false);
  const isOwnProfile = user?.id === profile?.id;
  
  // Fetch total likes received on bulletins
  const { data: likesCount, isLoading: likesLoading } = useQuery({
    queryKey: ['profile-likes-count', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return 0;
      
      const { data: bulletins } = await supabase
        .from('bulletins')
        .select('like_count')
        .eq('user_id', profile.id);

      return bulletins?.reduce((total, bulletin) => total + (bulletin.like_count || 0), 0) || 0;
    },
    enabled: !!profile?.id
  });
  useEffect(() => {
    if (profile?.updated_at || profile?.banner_image_url) {
      setImageError({
        retryCount: 0,
        hasError: false
      });
      setImageKey(Date.now());
    }
  }, [profile?.updated_at, profile?.banner_image_url]);
  const bannerUrl = getOptimizedImageUrl(profile?.banner_image_url);
  // Add cache-busting to banner similar to avatar to avoid stale cached images (especially after same-path overwrites)
  const bannerUrlWithKey = React.useMemo(() => {
    if (!bannerUrl) return null;
    const sep = bannerUrl.includes('?') ? '&' : '?';
    return `${bannerUrl}${sep}t=${imageKey}`;
  }, [bannerUrl, imageKey]);
  const getCleanAvatarUrl = () => {
    if (!profile?.profile_image_url) return null;
    const baseUrl = profile.profile_image_url.split('?')[0];
    return `${baseUrl}?t=${imageKey}`;
  };
  const avatarUrl = getCleanAvatarUrl();
  if (!profile) return null;
  // If in editing mode, display the edit form
  if (isEditing && isOwnProfile) {
    return (
      <div className="fixed inset-0 z-[100] min-h-screen bg-[#111418] overflow-y-auto flex justify-center">
        <div className="w-full max-w-2xl my-16 px-4">
          <div className="bg-[#1A1F2C]/80 backdrop-blur-sm p-6 rounded-xl border border-white/10 shadow-xl">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold">Edit Profile</h2>
              <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                <ChevronDown className="mr-2 h-4 w-4" />
                Back to Profile
              </Button>
            </div>
            <EditProfileForm 
              profile={profile} 
              onCancel={() => setIsEditing(false)} 
            />
          </div>
        </div>
      </div>
    );
  }

  return <div className="min-h-screen relative overflow-hidden">
      {/* Full-screen Banner Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A1F2C] via-[#2A2F3C] to-[#1A1F2C]" />
        
        {bannerUrlWithKey && !imageError.hasError && <div className="absolute inset-0">
            <img key={imageKey} src={bannerUrlWithKey} alt="Profile banner" className="w-full h-full object-cover" style={{
          objectPosition: 'center center'
        }} onError={e => handleImageError(e, setImageError, imageError, profile?.banner_image_url)} />
            <div className="absolute inset-0 bg-black/40" />
          </div>}
        
        {/* Gradient overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Smooth gradient transition to page background */}
        <div className="absolute bottom-0 left-0 right-0 h-96 bg-gradient-to-b from-transparent via-[#111418]/60 to-[#111418]" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-center items-center min-h-screen px-4 py-20 pb-24">
        {/* UI Section */}
        <div className="w-full max-w-2xl mx-auto">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: { opacity: 1, y: 0 }
            }}
            transition={{ duration: 0.6 }}
            className="space-y-6 text-center"
          >
            {/* Avatar - Responsive size */}
            <div className="relative flex justify-center">
              <div
                className={cn(
                  "rounded-full p-[3px] w-fit",
                  profile.is_admin && "bg-gradient-to-t from-[#F97316] to-[#FEF7CD]",
                  profile.is_premium && "bg-gradient-to-r from-[#1A1F2C] via-[#7E69AB] to-[#FFD700]"
                )}
              >
                <Avatar
                  className={cn(
                    "w-32 h-32 md:w-36 md:h-36",
                    !profile.is_admin && !profile.is_premium && "ring-2 ring-white/20",
                    "bg-[#1A1F2C]"
                  )}
                >
                  {avatarUrl && (
                    <AvatarImage
                      src={avatarUrl}
                      alt={profile.display_name || profile.username}
                      className="object-cover"
                    />
                  )}
                  <AvatarFallback
                    className="bg-gradient-to-br from-primary/20 to-primary/40 text-white text-xl md:text-xl font-bold"
                  >
                    {(profile.display_name || profile.username || "U").charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </div>
              
            </div>

            {/* Name and Username - Mobile responsive */}
            <div className="space-y-2">
              <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight">{profile.display_name || profile.username}</h1>
              {profile.display_name && (
                <p className="text-base md:text-lg text-gray-300 font-medium mb-2 md:mb-3 inline-flex items-center gap-2 justify-center">
                  <span>@{profile.username}</span>
                  {profile.is_admin && (
                    <VerifiedBadge type="admin" size={16} className="inline-block align-middle" shadow={false} />
                  )}
                  {profile.is_partner && (
                    <VerifiedBadge type="partner" size={16} className="inline-block align-middle" shadow={false} />
                  )}
                </p>
              )}
              <div
                className="flex items-center justify-center gap-1.5 sm:gap-2 mt-3 flex-wrap"
              >
                {/* User Number Badge - Mobile responsive */}
                {profile.registration_number && (
                  <div
                    className="inline-flex items-center gap-1 bg-black/50 border border-gray-500 rounded-full text-xs sm:text-sm font-medium text-white py-1 px-2 sm:py-[3px] sm:px-[9px] shadow-md shadow-black/20"
                  >
                    <Hash className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-300" />
                    <span className="text-white font-semibold">{profile.registration_number}</span>
                  </div>
                )}
                {/* Admin pill removed; verified badge now indicates status */}
                {/* Aura Score Badge - Mobile responsive */}
                <div
                  className="inline-flex items-center gap-1 bg-black/30 border border-white/20 rounded-full text-xs sm:text-sm font-medium text-white py-1 px-2 sm:py-[3px] sm:px-[9px]"
                >
                  <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  <span className="whitespace-nowrap">{eloScore ?? (profile.elo_rating || 1000)} Aura</span>
                </div>
                {/* Global Followers (manual) - Mobile responsive */}
                
              </div>
            </div>

            {/* Bio - Mobile responsive */}
            {profile.bio && (
              <motion.p
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { opacity: 0 },
                  visible: { opacity: 1 }
                }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="text-sm md:text-lg text-gray-300 max-w-lg mx-auto leading-relaxed px-4"
              >
                {profile.bio}
              </motion.p>
            )}

            {/* Stats Grid - Mobile responsive (2 rows on mobile if 4 items) */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
              }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className={cn(
                "grid gap-3 sm:gap-4 max-w-md mx-auto w-full",
                globalFollowers > 0 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"
              )}
            >
              {globalFollowers > 0 && (
                <div className="text-center">
                  <div className="flex items-center justify-center mb-1">
                    <Users className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <div className="text-base sm:text-xl font-bold text-white">
                    {globalFollowers.toLocaleString()}
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-400">
                    Global
                  </div>
                </div>
              )}
              <div 
                className="text-center cursor-pointer hover:opacity-80 transition-opacity" 
                onClick={() => {
                  setModalTab("followers");
                  setShowFollowersModal(true);
                }}
              >
                <div className="flex items-center justify-center mb-1">
                  <Users className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                </div>
                <div
                  className="text-base sm:text-xl font-bold text-white"
                >
                  {profile.followers_count || 0}
                </div>
                <div
                  className="text-[10px] sm:text-xs text-gray-400"
                >
                  Followers
                </div>
              </div>
              
              <div 
                className="text-center cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => {
                  setModalTab("following");
                  setShowFollowersModal(true);
                }}
              >
                <div className="flex items-center justify-center mb-1">
                  <Users className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                </div>
                <div
                  className="text-base sm:text-xl font-bold text-white"
                >
                  {profile.following_count || 0}
                </div>
                <div
                  className="text-[10px] sm:text-xs text-gray-400"
                >
                  Following
                </div>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <Heart className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                </div>
                <div
                  className="text-base sm:text-xl font-bold text-white"
                >
                  {likesLoading ? '...' : likesCount || 0}
                </div>
                <div
                  className="text-[10px] sm:text-xs text-gray-400"
                >
                  Likes
                </div>
              </div>
            </motion.div>

            {/* Action Buttons - Bigger */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
              }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="space-y-4"
            >
              {/* Profile Actions (Follow/Edit) */}
              <div className="flex justify-center">
                <ProfileActions 
                  isOwnProfile={isOwnProfile} 
                  onEdit={() => setIsEditing(true)} 
                  profileId={profile.id} 
                  profile={profile} 
                />
              </div>

              {/* Social Links Toggle - Normal size */}
              {profile.custom_links && profile.custom_links.length > 0 && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowSocialLinks(!showSocialLinks)} 
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20 transition-all duration-200 text-xs"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {showSocialLinks ? 'Hide Links' : 'Show Links'}
                </Button>
              )}

              {/* View Gallery CTA */}
              <button 
                onClick={onViewGallery} 
                className="group flex items-center gap-2 text-white/70 hover:text-white transition-all duration-300 text-sm font-medium mx-auto"
              >
                <span>view gallery</span>
                <ChevronDown className="w-4 h-4 group-hover:translate-y-1 transition-transform duration-300" />
              </button>
            </motion.div>

            {/* Expanded Social Links */}
            {showSocialLinks && (
              <motion.div 
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={{
                  hidden: { opacity: 0, height: 0 },
                  visible: { opacity: 1, height: 'auto' }
                }}
                transition={{ duration: 0.3 }}
                className="mt-2"
              >
                <ProfileSocialLinksExpanded profile={profile} />
              </motion.div>
            )}
          </motion.div>
        </div>

        
      </div>

      {/* Followers/Following Modal */}
      <FollowersModal
        profileId={profile.id}
        isOpen={showFollowersModal}
        onClose={() => setShowFollowersModal(false)}
        initialTab={modalTab}
      />
    </div>;
};
export default ProfileLandingPage;